import {useCallback, useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Fix a practitioner's or business's text yourself (typos, wording):
// business name, names, specialty, bio, services and classes. Editing just
// one language re-translates the other automatically (can be turned off).

const PROFILE_FIELDS = [
  {key: 'businessName', label: 'Business name', businessOnly: true},
  {key: 'firstName', label: 'First name', half: true},
  {key: 'lastName', label: 'Last name', half: true},
  {key: 'specialty', label: 'Specialty (English)', half: true},
  {key: 'specialtyEs', label: 'Specialty (Spanish)', half: true},
  {key: 'bio', label: 'Bio (English)', multiline: true, half: true},
  {key: 'bioEs', label: 'Bio (Spanish)', multiline: true, half: true},
  {
    key: 'websitePageUrl',
    label: 'Their page on the EnForma website (e.g. https://sayulita.enformawellness.com/their-name) - the booking website links to it',
  },
]
const SERVICE_FIELDS = [
  {key: 'serviceName', label: 'Service name (English)', half: true},
  {key: 'serviceNameEs', label: 'Service name (Spanish)', half: true},
  {key: 'desc', label: 'Description (English)', multiline: true, half: true},
  {key: 'descEs', label: 'Description (Spanish)', multiline: true, half: true},
]
const CLASS_FIELDS = [
  {key: 'name', label: 'Class name (English)', half: true},
  {key: 'nameEs', label: 'Class name (Spanish)', half: true},
  {key: 'description', label: 'Description (English)', multiline: true, half: true},
  {key: 'descriptionEs', label: 'Description (Spanish)', multiline: true, half: true},
]

function TextBlock({title, subtitle, fields, original, url, autoTranslate, extra, onSaved}) {
  const [values, setValues] = useState({})
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const v = {}
    fields.forEach((f) => (v[f.key] = original?.[f.key] || ''))
    setValues(v)
  }, [original, fields])

  const changed = fields.filter((f) => (values[f.key] ?? '') !== (original?.[f.key] || '')).map((f) => f.key)

  const save = async () => {
    setSaving(true)
    setMsg('')
    setError('')
    try {
      const body = {autoTranslate, fields: {}, ...(extra || {})}
      changed.forEach((k) => (body.fields[k] = values[k]))
      const {data} = await axios.put(`${BASE_URL}${url}`, body)
      setMsg(data.updated > 1 ? `Saved (${data.updated} upcoming sessions updated).` : 'Saved.')
      onSaved && onSaved()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='border rounded p-5 mb-5'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div>
          <div className='fw-bolder fs-5'>{title}</div>
          {subtitle && <div className='text-muted fs-7'>{subtitle}</div>}
        </div>
        <button className='btn btn-sm btn-primary' disabled={!changed.length || saving} onClick={save}>
          {saving ? 'Saving...' : changed.length ? `Save ${changed.length} change${changed.length > 1 ? 's' : ''}` : 'Save'}
        </button>
      </div>
      {msg && <div className='alert alert-success py-2'>{msg}</div>}
      {error && <div className='alert alert-danger py-2'>{error}</div>}
      <div className='row'>
        {fields.map((f) => (
          <div key={f.key} className={`${f.half ? 'col-md-6' : 'col-12'} mb-4`}>
            <label className='form-label fs-7 text-muted mb-1'>
              {f.label}
              {changed.includes(f.key) && <span className='badge badge-light-warning ms-2'>edited</span>}
            </label>
            {f.multiline ? (
              <textarea
                className='form-control form-control-solid'
                rows={6}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues({...values, [f.key]: e.target.value})}
              />
            ) : (
              <input
                className='form-control form-control-solid'
                value={values[f.key] ?? ''}
                onChange={(e) => setValues({...values, [f.key]: e.target.value})}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const EditText = () => {
  const [params, setParams] = useSearchParams()
  const userId = params.get('user')
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [autoTranslate, setAutoTranslate] = useState(true)

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([])
      return
    }
    const t = setTimeout(async () => {
      try {
        const {data} = await axios.get(`${BASE_URL}/admin/text/search`, {params: {q}})
        setResults(data.data || [])
      } catch (e) {
        setResults([])
      }
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  const load = useCallback(async () => {
    if (!userId) {
      setData(null)
      return
    }
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/text/${userId}`)
      setData(data.data)
    } catch (e) {
      setError('Could not load this profile.')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    load()
  }, [load])

  const isBusiness = data?.user?.role === 'BUSINESS' || data?.user?.availableRoles?.includes('BUSINESS')
  const profileFields = PROFILE_FIELDS.filter((f) => !f.businessOnly || isBusiness || data?.user?.businessName)

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Edit Practitioner Text</h3>
        </div>
      </div>
      <div className='card-body border-top p-9'>
        <p className='text-muted'>
          Fix typos or wording in a practitioner's or business's profile, services and classes. Changes show
          in the app straight away. If you only edit one language, the other is translated again
          automatically.
        </p>
        <div className='position-relative mb-6' style={{maxWidth: 520}}>
          <input
            className='form-control form-control-solid'
            placeholder='Search by name, business name or email...'
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {results.length > 0 && (
            <div className='border rounded bg-body shadow-sm position-absolute w-100 mt-1' style={{zIndex: 10, maxHeight: 320, overflowY: 'auto'}}>
              {results.map((r) => (
                <div
                  key={r._id}
                  className='px-4 py-3 border-bottom cursor-pointer bg-hover-light'
                  onClick={() => {
                    setParams({user: r._id})
                    setQ('')
                    setResults([])
                  }}>
                  <div className='fw-bold'>{r.name || '(no name)'}</div>
                  <div className='text-muted fs-7'>
                    {r.email} {r.town ? `· ${r.town}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading && <div className='text-muted'>Loading...</div>}
        {error && <div className='alert alert-danger'>{error}</div>}

        {data && !loading && (
          <>
            <div className='d-flex align-items-center mb-5'>
              {data.user.profileImageUrl && (
                <img src={data.user.profileImageUrl} alt='' className='rounded-circle me-4' style={{width: 56, height: 56, objectFit: 'cover'}} />
              )}
              <div className='flex-grow-1'>
                <div className='fw-bolder fs-3'>{data.user.name}</div>
                <div className='text-muted'>
                  {data.user.email} {data.user.primaryLocation ? `· ${data.user.primaryLocation}` : ''}
                </div>
              </div>
              <label className='form-check form-switch form-check-custom form-check-solid'>
                <input className='form-check-input' type='checkbox' checked={autoTranslate} onChange={(e) => setAutoTranslate(e.target.checked)} />
                <span className='form-check-label text-muted fs-7'>Re-translate the other language when I edit only one</span>
              </label>
            </div>

            <h4 className='mb-3'>Profile</h4>
            <TextBlock title='Profile' fields={profileFields} original={data.user} url={`/admin/text/user/${data.user._id}`} autoTranslate={autoTranslate} onSaved={load} />

            <h4 className='mb-3 mt-8'>Services ({data.services.length})</h4>
            {!data.services.length && <div className='text-muted mb-5'>No services.</div>}
            {data.services.map((s) => (
              <TextBlock key={s._id} title={s.serviceName || s.serviceNameEs || 'Service'} fields={SERVICE_FIELDS} original={s} url={`/admin/text/service/${s._id}`} autoTranslate={autoTranslate} onSaved={load} />
            ))}

            <h4 className='mb-3 mt-8'>Upcoming classes ({data.classes.length})</h4>
            {!data.classes.length && <div className='text-muted mb-5'>No upcoming classes.</div>}
            {data.classes.map((c) => (
              <TextBlock
                key={c._id}
                title={c.name || c.nameEs || 'Class'}
                subtitle={`Next: ${new Date(c.startTime).toLocaleString()}${c.sessions > 1 ? ` · saving updates all ${c.sessions} upcoming sessions` : ''}`}
                fields={CLASS_FIELDS}
                original={c}
                url={`/admin/text/class/${c._id}`}
                autoTranslate={autoTranslate}
                onSaved={load}
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

export default EditText
